import { useTranslation } from "react-i18next";
import { localized, useAppLanguage } from "../utils/localized";

import { BasketItem } from "../store/slices/basketSlice";
import { ReplacementEntry } from "../modules/basket/libs/replacementLog";
import { XIcon } from "../assets/X";
import { DeleteIcon } from "../assets/Delete";
import { impactOccuer } from "../utils/hapticEvents";
import { ProductImage } from "./ProductImage";

type IProps = {
  items: BasketItem[];
  /** Swaps already applied, kept around so they can still be undone. */
  replacements: ReplacementEntry[];
  /** Set once the user picks something to replace; drives the detailed state. */
  activeItem?: BasketItem;
  onOpen: () => void;
  /** Removes the previewed item straight out of the basket. */
  onRemove: (item: BasketItem) => void;
  /** Drops the swap summary once the user is done looking at it. */
  onDismiss: () => void;
};

/**
 * Pinned above the tab bar while the user browses for replacements, so the
 * unavailable items never scroll out of sight. Collapsed by design — the full
 * list with per-item actions lives in UnavailableItemsDrawer, one tap away.
 */
export const ReplaceUnavailableBar = ({
  items,
  replacements,
  activeItem,
  onOpen,
  onRemove,
  onDismiss,
}: IProps) => {
  const { t } = useTranslation();
  const lang = useAppLanguage();

  // Swapping out the last unavailable item used to hide the bar instantly,
  // stranding the user with no way back to what they'd just changed — the
  // drawer, and the undo in it, is only reachable from here.
  if (!items.length && !replacements.length) return null;

  const remaining = items.length - 1;
  const preview = activeItem ?? items[0] ?? replacements[0]?.from ?? undefined;
  const previewImage = items.length
    ? preview?.menu_image
    : replacements[0]?.toImage;

  // Nothing left to fix — the bar is just a receipt at this point, so its
  // control closes it instead of cancelling anything.
  const isSummary = !items.length;

  return (
    // 16px gutters per the spec, no top padding, 2px down to the nav — the
    // nav drops its own top padding whenever this slot is filled. The strip is
    // opaque so page content doesn't scroll through the gap around the card:
    // `fill-background` is the #F0F0F0 the design asks for, as a token rather
    // than a literal so dark mode still works.
    <div className="bg-fill-background px-4 pb-[2px]">
      <div
        // Sits directly on the strip's backdrop — no card fill, and no shadow
        // either: with nothing filled behind it, the drop shadow rendered as a
        // halo around an invisible box.
        className="flex h-[72px] flex-row items-center gap-x-3 rounded-2xl px-4 transition-opacity active:opacity-70"
        onClick={() => {
          impactOccuer("soft");
          onOpen();
        }}
      >
        {/* 56×56 with a 12px radius, per the spec. The background sits on the
            image itself rather than on a wrapper so those are the real
            dimensions of the element — product shots are transparent PNGs and
            would otherwise have no edge. */}
        <ProductImage
          src={previewImage}
          alt=""
          className="h-14 w-14 shrink-0 rounded-xl bg-fill-background object-contain p-2"
        />

        <div className="flex min-w-0 flex-1 flex-col gap-y-[2px]">
          {activeItem ? (
            <>
              <p className="truncate text-body-base-medium">
                {localized(lang, {
                  ru: activeItem.title,
                  uz: activeItem.title_uz,
                  en: activeItem.title_en,
                })}
              </p>
              <p className="truncate text-caption-sm-medium text-text-tertiary">
                {t("replacement-label")}
                {remaining > 0 &&
                  ` · ${t("unavailable-more", { count: remaining })}`}
              </p>
            </>
          ) : items.length ? (
            <>
              <p className="truncate text-body-sm-medium">
                {items.length}{" "}
                {items.length === 1 ? t("position-one") : t("position-many")}
              </p>
              <p className="truncate text-caption-sm-medium text-fill-danger">
                {t("not-available")}
              </p>
            </>
          ) : (
            <>
              {/* Reports what happened — nothing here is waiting on approval. */}
              <p className="truncate text-body-sm-medium">
                {t("replaced-count", { count: replacements.length })}
              </p>
              <p className="truncate text-caption-sm-medium text-brand-green">
                {t("replaced")}
              </p>
            </>
          )}
        </div>

        {/* Nothing here is waiting on approval — every state but the receipt
            can drop the previewed item straight from the bar. Once there's
            nothing left to fix the same spot just dismisses the receipt. */}
        {isSummary ? (
          <button
            type="button"
            aria-label={t("cancel")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fill-background active:opacity-70"
            onClick={(event) => {
              event.stopPropagation();
              impactOccuer("light");
              onDismiss();
            }}
          >
            <XIcon className="text-text-icon-secondary" />
          </button>
        ) : (
          <button
            type="button"
            aria-label={t("remove-item")}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fill-base active:opacity-70"
            onClick={(event) => {
              event.stopPropagation();
              impactOccuer("light");
              onRemove(activeItem ?? items[0]);
            }}
          >
            <DeleteIcon className="h-[18px] w-[18px] text-text-icon-secondary" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ReplaceUnavailableBar;
